// src/components/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [forms, setForms] = useState<any[]>([]);
  const [contracts, setContracts] = useState<any[]>([]); // Added state for contracts
  const [clients, setClients] = useState<any[]>([]);
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
    fetchForms();
    fetchContracts(); // Added fetch for contracts
    fetchClients();
    fetchVenues();
  }, []);

  useEffect(() => {
    let updatedClients = clients;
    
    if (clientSearchQuery.trim() !== '') {
      const query = clientSearchQuery.toLowerCase();
      updatedClients = clients.filter(client => client.name.toLowerCase().includes(query));
    }

    updatedClients = [...updatedClients].sort((a, b) => {
      const dateA = new Date(a.eventDate).getTime();
      const dateB = new Date(b.eventDate).getTime();
      return dateA - dateB;
    });

    setFilteredClients(updatedClients);
  }, [clientSearchQuery, clients]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(response.data);
    } catch (error: any) {
      console.error('Failed to fetch templates:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError('Failed to load templates.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchForms = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Forms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForms(response.data);
    } catch (error: any) {
      console.error('Failed to fetch forms:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError('Failed to load forms.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchContracts = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Contracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContracts(response.data);
    } catch (error: any) {
      console.error('Failed to fetch contracts:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError('Failed to load contracts.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clientData = response.data;
      clientData.forEach((client: any) => {
        if (client.submittedPayments && typeof client.submittedPayments === 'string') {
          try {
            client.submittedPayments = JSON.parse(client.submittedPayments);
          } catch (parseError) {
            console.error('Failed to parse submittedPayments:', client.id, parseError);
            client.submittedPayments = [];
          }
        } else {
          client.submittedPayments = [];
        }
      });
      setClients(clientData);
    } catch (error: any) {
      console.error('Failed to fetch clients:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError('Failed to load clients.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVenues = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Venues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVenues(response.data);
    } catch (error: any) {
      console.error('Failed to fetch venues:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError('Failed to load venues.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <style>
        {`
          .dashboard-card {
            height: 400px;
            display: flex;
            flex-direction: column;
          }
          .dashboard-card-body {
            flex: 1;
            max-height: 300px;
            overflow-y: auto;
            padding: 1rem;
          }
          .dashboard-card-body::-webkit-scrollbar {
            width: 8px;
          }
          .dashboard-card-body::-webkit-scrollbar-thumb {
            background-color: #007bff;
            border-radius: 4px;
          }
          .dashboard-card-body::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
        `}
      </style>
      <h2 className="mb-4 text-center">Dashboard</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {isLoading && <div className="alert alert-info">Loading...</div>}
      <div className="row">
        <div className="col-md-4 mb-4">
          <div className="card shadow-sm dashboard-card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Templates ({templates.length})</h4>
            </div>
            <div className="card-body dashboard-card-body">
              {templates.length === 0 ? (
                <p className="text-muted text-center">No templates found.</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {templates.map(template => (
                    <li key={template.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {template.name}
                      <Link to={`/edit-template/${template.id}`} className="btn btn-outline-primary btn-sm">
                        Edit
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-4">
          <div className="card shadow-sm dashboard-card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Forms ({forms.length})</h4>
            </div>
            <div className="card-body dashboard-card-body">
              {forms.length === 0 ? (
                <p className="text-muted text-center">No forms found.</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {forms.map(form => (
                    <li key={form.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {form.name} ({form.id}), Status: {form.status}
                      <Link to={`/form-details/${form.id}`} className="btn btn-outline-primary btn-sm">
                        View Details
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-4">
          <div className="card shadow-sm dashboard-card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Contracts ({contracts.length})</h4>
            </div>
            <div className="card-body dashboard-card-body">
              {contracts.length === 0 ? (
                <p className="text-muted text-center">No contracts found.</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {contracts.map(contract => (
                    <li key={contract.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {contract.name} ({contract.id}), Status: {contract.status}
                      <Link to={`/contract-details/${contract.id}`} className="btn btn-outline-primary btn-sm">
                        View Details
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-4">
          <div className="card shadow-sm dashboard-card">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Clients ({filteredClients.length})</h4>
              <input
                type="text"
                className="form-control w-50"
                placeholder="Search by name..."
                value={clientSearchQuery}
                onChange={(e) => setClientSearchQuery(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="card-body dashboard-card-body">
              {filteredClients.length === 0 ? (
                <p className="text-muted text-center">
                  {clientSearchQuery ? 'No clients match your search.' : 'No clients found.'}
                </p>
              ) : (
                <ul className="list-group list-group-flush">
                  {filteredClients.map(client => (
                    <li key={client.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <div>{client.name}</div>
                        <small className="d-block text-muted">
                          Event Date: {new Date(client.eventDate).toLocaleDateString()}
                        </small>
                        <small className="d-block text-muted">
                          Contract Price: ${client.contractPrice.toFixed(2)}
                        </small>
                      </div>
                      <Link to={`/clients/${client.id}`} className="btn btn-outline-primary btn-sm">
                        View Details
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-4">
          <div className="card shadow-sm dashboard-card">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">Venues ({venues.length})</h4>
            </div>
            <div className="card-body dashboard-card-body">
              {venues.length === 0 ? (
                <p className="text-muted text-center">No venues found.</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {venues.map(venue => (
                    <li key={venue.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <div>{venue.name}</div>
                        <small className="d-block text-muted">
                          Base Past Events: {venue.pastEventsBase}
                        </small>
                        <small className="d-block text-muted">
                          Total Past Events: {venue.pastEvents}
                        </small>
                        <small className="d-block text-muted">
                          Upcoming Events: {venue.upcomingEvents}
                        </small>
                      </div>
                      <Link to={`/venues/${venue.id}`} className="btn btn-outline-primary btn-sm">
                        View Details
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;